import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  BookOpen,
  ArrowRight,
  Wand2,
  Briefcase,
  EyeOff,
  Film,
  GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useStealth } from "../context/StealthContext";
import { fetchRoutines, fetchArsenal } from "../lib/api";
import { stealthListRoutines, stealthCounts, stealthWipeAll } from "../lib/stealthDb";

const GOAL_LABELS = {
  soft: "Soft & natural",
  everyday: "Everyday",
  glam: "Glam",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * Home — the warm landing page returning users see right after sign-in.
 *
 * Three equal-weight "doors": Try a new glow / Go back to an old routine /
 * My Beauty Arsenal. When Stealth Mode is on, a soft pill appears just
 * above the greeting that one-taps the user out of stealth (with confirm)
 * — so the toggle isn't buried in Settings.
 *
 * All strings are i18n-routed (en / fr / es). Goal labels stay English
 * for now because they're enum tokens, not user-facing chrome.
 */
const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stealth, setStealth } = useStealth();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arsenalCount, setArsenalCount] = useState(null);
  const [localCounts, setLocalCounts] = useState({ routines: 0, snapshots: 0 });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = stealth
          ? (await stealthListRoutines()).map((r) => ({
              id: r.id,
              intro: r.intro,
              goal: r.goal,
              created_at: r.created_at,
              step_count: r.steps?.length ?? 0,
            }))
          : await fetchRoutines();
        if (alive) setRoutines(data || []);
      } catch {
        if (alive) setRoutines([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    // Fire-and-forget arsenal count for the door badge.
    (async () => {
      if (stealth) return;
      try {
        const a = await fetchArsenal();
        if (alive) setArsenalCount((a?.items || []).length);
      } catch {
        if (alive) setArsenalCount(null);
      }
    })();
    // Local stealth-store counts — used by the off-toggle confirm prompt
    // so the user knows exactly what gets wiped when they leave stealth.
    if (stealth) {
      stealthCounts().then((c) => { if (alive) setLocalCounts(c); }).catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [stealth]);

  // First-name greeting; falls back to a friendly term-of-endearment
  // ("gorgeous" / "magnifique" / "preciosa") when we don't have a name.
  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const greeting = firstName || t("home.greetingFallback");

  const recent = routines.slice(0, 2);
  const hasRoutines = !loading && routines.length > 0;
  const hasArsenal = arsenalCount !== null && arsenalCount > 0;

  // Toggle Stealth OFF directly from the home chip. Mirrors the wipe
  // confirmation in Settings so a one-tap toggle can't silently lose
  // local-only routines/snapshots the user forgot about.
  const onTurnOffStealth = async () => {
    if (!stealth) return;
    if (localCounts.routines > 0 || localCounts.snapshots > 0) {
      const ok = window.confirm(
        t("stealthConfirm.off", {
          routines: localCounts.routines,
          snapshots: localCounts.snapshots,
        })
      );
      if (!ok) return;
      try {
        await stealthWipeAll();
        setLocalCounts({ routines: 0, snapshots: 0 });
      } catch {
        toast.error(t("common.tryAgain"));
        return;
      }
    }
    setStealth(false);
    toast.success(t("settings.stealthToastOff"));
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <section className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Hero greeting */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 sm:mb-14"
          >
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#9B6A6C]">
                <Sparkles className="h-3.5 w-3.5" />
                {t("home.welcomeBack")}
              </span>
              {stealth && (
                <button
                  type="button"
                  onClick={onTurnOffStealth}
                  title={t("home.stealthChipTooltip")}
                  className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2C2123] text-[#FAF6F2] text-[11px] uppercase tracking-[0.18em] hover:bg-[#3a2b2e] transition-colors"
                  data-testid="home-stealth-chip"
                  aria-label={t("home.stealthChipTooltip")}
                >
                  <EyeOff className="h-3 w-3 text-[#D5A8A4]" />
                  {t("home.stealthChipOn")}
                  <span className="ml-0.5 text-[#D5A8A4] group-hover:text-white">×</span>
                </button>
              )}
            </div>
            <h1
              className="text-4xl sm:text-5xl font-light text-[#2C2123] leading-[1.1]"
              data-testid="home-greeting"
            >
              {t("home.greeting", { name: greeting })}{" "}
              <span className="italic font-serif text-[#9B6A6C]">
                {t("home.vibeQuestion")}
              </span>
            </h1>
            <p className="text-[#5C4B51] mt-3 max-w-xl text-base sm:text-lg leading-relaxed">
              {t("home.subline")}
            </p>
          </motion.div>

          {/* Five equal-weight launchpad doors. On lg+ this lays out
              as 3 across + 2 across (the last row centers the pair).
              On sm-md it's 2 across; on mobile it stacks. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14" data-testid="home-launchpad-grid">
            {/* Door 1 — Try a new glow (selfie scan → /onboarding) */}
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              onClick={() => navigate("/onboarding?edit=1")}
              className="group text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F5DCD7] via-[#EAC9C2] to-[#D5A8A4] p-7 sm:p-9 min-h-[280px] hover:-translate-y-0.5 transition-all soft-shadow-lg"
              data-testid="home-try-new-glow"
            >
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex-1">
                  <div className="h-12 w-12 rounded-full bg-white/40 backdrop-blur-sm flex items-center justify-center mb-6">
                    <Wand2 className="h-5 w-5 text-[#5D4050]" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-light text-[#2C2123] leading-tight mb-2">
                    {t("home.doorNewTitle")}
                  </h2>
                  <p className="text-[#5C4B51] text-sm sm:text-base leading-relaxed max-w-xs">
                    {t("home.doorNewBody")}
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm text-[#5D4050] group-hover:gap-2.5 transition-all font-medium">
                  {t("home.doorNewCta")}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
              <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/25 blur-2xl pointer-events-none" />
            </motion.button>

            {/* Door 2 — Recreate a look (URL adapter → /recreate). New
                tile added Feb 26, 2026: this flow has shipped for weeks
                but never had a home-page surface. Pro · 7-day trial pill
                added Feb 27 — Recreate-a-Look's generation step is
                Pro-gated (uses /api/analyze/from-saved-profile which
                checks paywall), so we surface that up front instead of
                letting users hit a 402 from the inside. Stealth-safe:
                the Recreate page itself falls back to "drop your link
                + describe the vibe" when the user has no saved face
                profile. */}
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.075 }}
              onClick={() => navigate("/recreate")}
              className="group text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#EADDD7] via-[#E1CFC7] to-[#C8B0A6] p-7 sm:p-9 min-h-[280px] hover:-translate-y-0.5 transition-all soft-shadow-lg"
              data-testid="home-recreate-door"
            >
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-12 w-12 rounded-full bg-white/45 backdrop-blur-sm flex items-center justify-center">
                      <Film className="h-5 w-5 text-[#5D4050]" />
                    </div>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#5D4050] bg-white/60 backdrop-blur-sm px-2 py-1 rounded-full"
                      data-testid="home-recreate-locked-hint"
                    >
                      <Sparkles className="h-3 w-3" />
                      {t("home.doorTechniquesLockedHint")}
                    </span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-light text-[#2C2123] leading-tight mb-2">
                    {t("home.doorRecreateTitle")}
                  </h2>
                  <p className="text-[#5C4B51] text-sm sm:text-base leading-relaxed max-w-xs">
                    {t("home.doorRecreateBody")}
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm text-[#5D4050] group-hover:gap-2.5 transition-all font-medium">
                  {t("home.doorRecreateCta")}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
              <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/25 blur-2xl pointer-events-none" />
            </motion.button>

            {/* Door 3 — Techniques library (Pro). New tile added
                Feb 26, 2026. Visible to all users; the page itself
                shows lock overlays + 7-day-trial CTA for free users
                (existing behaviour in /techniques). We don't gate the
                tile here because a) we WANT free users to see the
                library exists, and b) the trial sign-up flow lives
                behind the click. */}
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={() => navigate("/techniques")}
              className="group text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F2E8E9] via-[#E8D5D5] to-[#D1B2B0] p-7 sm:p-9 min-h-[280px] hover:-translate-y-0.5 transition-all soft-shadow-lg"
              data-testid="home-techniques-door"
            >
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-12 w-12 rounded-full bg-white/45 backdrop-blur-sm flex items-center justify-center">
                      <GraduationCap className="h-5 w-5 text-[#5D4050]" />
                    </div>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#5D4050] bg-white/60 backdrop-blur-sm px-2 py-1 rounded-full"
                      data-testid="home-techniques-locked-hint"
                    >
                      <Sparkles className="h-3 w-3" />
                      {t("home.doorTechniquesLockedHint")}
                    </span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-light text-[#2C2123] leading-tight mb-2">
                    {t("home.doorTechniquesTitle")}
                  </h2>
                  <p className="text-[#5C4B51] text-sm sm:text-base leading-relaxed max-w-xs">
                    {t("home.doorTechniquesBody")}
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm text-[#5D4050] group-hover:gap-2.5 transition-all font-medium">
                  {t("home.doorTechniquesCta")}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
              <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/25 blur-2xl pointer-events-none" />
            </motion.button>

            {/* Door 4 — Go back to an old routine */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.125 }}
              className="relative overflow-hidden rounded-3xl bg-white border border-[#EADDD7] p-7 sm:p-9 min-h-[280px] flex flex-col soft-shadow"
              data-testid="home-saved-routines"
            >
              <div className="flex-1">
                <div className="h-12 w-12 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-6">
                  <BookOpen className="h-5 w-5 text-[#9B6A6C]" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-[#2C2123] leading-tight mb-2">
                  {t("home.doorOldTitle")}
                </h2>
                <p className="text-[#5C4B51] text-sm sm:text-base leading-relaxed">
                  {hasRoutines
                    ? t("home.doorOldBodyHas")
                    : t("home.doorOldBodyEmpty")}
                </p>
              </div>

              {loading ? (
                <div className="mt-5 space-y-2">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="h-10 rounded-lg bg-[#F8EEEE] animate-pulse"
                    />
                  ))}
                </div>
              ) : hasRoutines ? (
                <div className="mt-5 space-y-1">
                  {recent.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => navigate(`/routine/${r.id}`)}
                      className="w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-[#FAF5F1] border border-transparent hover:border-[#EADDD7] transition-all group/item"
                      data-testid={`home-recent-routine-${r.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[#2C2123] truncate">
                          {GOAL_LABELS[r.goal] || r.goal || "Routine"}
                          <span className="text-[#8A7D82] font-normal">
                            {" · "}
                            {fmtDate(r.created_at)}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-[#8A7D82] group-hover/item:text-[#9B6A6C] shrink-0" />
                    </button>
                  ))}
                  <Link
                    to="/my-routines"
                    className="inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#7d5455] mt-2 pl-3 transition-all"
                    data-testid="home-see-all-routines"
                  >
                    {t("home.doorOldSeeAll", { count: routines.length })}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ) : (
                <Link
                  to="/onboarding"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#7d5455] font-medium transition-all"
                  data-testid="home-create-first"
                >
                  {t("home.doorOldCreateFirst")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </motion.div>

            {/* Door 5 — Beauty Arsenal. Always visible (Feb 26, 2026):
                the founder explicitly asked for the Arsenal as a 5th
                always-on tile, so the stealth-mode gate was removed.
                In stealth, the empty-state copy is shown because
                nothing is synced to the cloud; clicking through to
                /arsenal still lets the user add items locally. */}
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              onClick={() => navigate("/arsenal")}
              className="group text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F8EFE9] via-[#F2E2DC] to-[#E5C9C0] p-7 sm:p-9 min-h-[280px] hover:-translate-y-0.5 transition-all soft-shadow-lg"
              data-testid="home-arsenal-door"
            >
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex-1">
                  <div className="h-12 w-12 rounded-full bg-white/55 backdrop-blur-sm flex items-center justify-center mb-6">
                    <Briefcase className="h-5 w-5 text-[#7d5455]" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-light text-[#2C2123] leading-tight mb-2">
                    {t("home.doorArsenalTitle")}
                    {hasArsenal && (
                      <span
                        className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#9B6A6C] text-white text-[11px] font-medium align-middle"
                        data-testid="home-arsenal-count"
                      >
                        {arsenalCount}
                      </span>
                    )}
                  </h2>
                  <p className="text-[#5C4B51] text-sm sm:text-base leading-relaxed max-w-xs">
                    {hasArsenal
                      ? t("home.doorArsenalBodyHas")
                      : t("home.doorArsenalBodyEmpty")}
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm text-[#7d5455] group-hover:gap-2.5 transition-all font-medium">
                  {hasArsenal
                    ? t("home.doorArsenalCtaHas")
                    : t("home.doorArsenalCtaEmpty")}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
              <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/30 blur-2xl pointer-events-none" />
            </motion.button>
          </div>

          {/* Soft footer note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center text-sm text-[#8A7D82]"
          >
            <span className="italic">{t("home.footerSettingsHint")} </span>
            <Link
              to="/settings"
              className="text-[#9B6A6C] hover:text-[#7d5455] underline underline-offset-2"
              data-testid="home-settings-link"
            >
              {t("home.footerSettingsLink")}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
